import {
  Box,
  Heading,
  VStack,
  Text,
  Divider,
  Button,
  HStack,
  Spacer,
} from "@chakra-ui/react"
import useMemberStore from "@/store/MemberStore"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import { useTranslations } from "next-intl"
import Head from "next/head"
import NotificationKeywordList from "@/components/Keyword/NotificationKeywordList"
import useKeywordNotificationListStore from "@/store/KeywordNotificationListStore"
import useKeywordPermissionStore from "@/store/KeywordPermissionStore"
import { useBreakpointValue } from "@chakra-ui/react"
import { AddIcon } from "@chakra-ui/icons"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import CreateKeywordComponent from "@/components/Keyword/CreateKeywordComponent"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import Link from "next/link"

function ThreadsKeywordNotifications() {
  const t = useTranslations("KeywordNotificationDashboard")
  const title = t("Meta.title")
  const isLoggedIn = useMemberStore((state) => state.checkIsLoggedIn())
  const member = useMemberStore((state) => state.member)
  const router = useRouter()
  const {
    keywords,
    isLoading: isLoadingKeywords,
    fetchKeywordNotifications,
  } = useKeywordNotificationListStore()
  const margin = useBreakpointValue({ base: "16px", md: "0px" })
  const [isLoading, setIsLoading] = useState(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [showPermissionAlert, setShowPermissionAlert] = useState(false)
  const [showConnectAlert, setShowConnectAlert] = useState(false)
  const checkPermission = useKeywordPermissionStore(
    (state) => state.checkPermission,
  )
  const hasPermission = useKeywordPermissionStore(
    (state) => state.hasPermission,
  )
  const getCurrentSelectedThreadsAccountId = useMemberStore(
    (state) => state.getCurrentSelectedThreadsAccountId,
  )

  useEffect(() => {
    if (!isLoggedIn) {
      router.push("/signup")
      return
    }
    fetchKeywordNotifications()
  }, [isLoggedIn, router, fetchKeywordNotifications])

  const handlePermissionCheck = async () => {
    const isPro = member?.subscribedVipPlan === "pro"
    const hasConnectedAccount =
      member?.connectedThreadsAccounts &&
      member.connectedThreadsAccounts.length > 0

    if (!hasConnectedAccount && !isPro) {
      setShowConnectAlert(true)
      return
    }

    if (hasPermission) {
      setIsPopoverOpen(true)
      return
    }

    const threadsUserId = getCurrentSelectedThreadsAccountId()
    if (!threadsUserId) {
      setShowConnectAlert(true)
      return
    }

    setIsLoading(true)
    const permissionResult = await checkPermission(threadsUserId)
    if (permissionResult) {
      setIsPopoverOpen(true)
    } else {
      setShowPermissionAlert(true)
    }
    setIsLoading(false)
  }

  return (
    <Box bg="ui.foreground1" marginLeft={margin} marginRight={margin}>
      <Head>
        <title>{title}</title>
        <meta name="og:title" content={title} />
      </Head>
      {showPermissionAlert && (
        <NoPermissionAlert onClose={() => setShowPermissionAlert(false)} />
      )}
      {showConnectAlert && (
        <NoConnectedAccountAlert onClose={() => setShowConnectAlert(false)} />
      )}
      <Box mx="auto" w="100%" marginTop="36px">
        <VStack spacing="16px" marginLeft="4px" marginRight="4px">
          <Box width="100%">
            <Heading>{title}</Heading>
            <HStack align="start" mt={2}>
              <Text fontSize="16px" color="text.semidark">
                {t("description")}
              </Text>
              <Spacer />
              <Popover open={isPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="danger"
                    leftIcon={<AddIcon />}
                    onClick={handlePermissionCheck}
                    isLoading={isLoading}
                    loadingText={t("Message.checkingPermission")}>
                    {t("Actions.addKeyword")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[calc(100vw-2rem)] sm:w-[480px] overflow-y-auto ml-2"
                  onInteractOutside={() => setIsPopoverOpen(false)}>
                  <CreateKeywordComponent />
                </PopoverContent>
              </Popover>
            </HStack>
            <Divider className="mt-4" />
            <Box mt={4}>
              <VStack spacing="16px" align="left">
                <NotificationKeywordList data={{ data: keywords }} />
              </VStack>
            </Box>
          </Box>
        </VStack>
      </Box>
    </Box>
  )
}

function NoPermissionAlert({ onClose }: { onClose: () => void }) {
  const t = useTranslations("KeywordCreate")
  const router = useRouter()
  const { locale } = router
  const redirectUri =
    locale == "zh-TW"
      ? `${process.env.NEXT_PUBLIC_THREADS_REDIRECT_URI_TW}?redirect=keyword`
      : `${process.env.NEXT_PUBLIC_THREADS_REDIRECT_URI_EN}?redirect=keyword`
  const url = `${process.env.NEXT_PUBLIC_THREADS_CALLBACK_WITH_SEARCH_URI}&redirect_uri=${redirectUri}`

  return (
    <AlertDialog open>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("Alerts.needPermission.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("Alerts.needPermission.message")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>
            {t("Action.cancel")}
          </AlertDialogCancel>
          <Link href={url}>
            <Button background="main.red">
              {t("Alerts.needPermission.action")}
            </Button>
          </Link>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function NoConnectedAccountAlert({ onClose }: { onClose: () => void }) {
  const t = useTranslations("KeywordDashboard")
  return (
    <AlertDialog open>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("Alerts.needConnectAccount.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("Alerts.needConnectAccount.message")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4">
          <AlertDialogCancel onClick={onClose}>
            {t("Actions.cancel")}
          </AlertDialogCancel>
          <Link href="https://instagram.com/threadslytics">
            <AlertDialogAction>{t("Actions.contactUs")}</AlertDialogAction>
          </Link>
          <Link href="/threads/connect">
            <AlertDialogAction>{t("Actions.connect")}</AlertDialogAction>
          </Link>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      messages: (await import(`@/messages/${locale}.json`)).default,
    },
  }
}

export default ThreadsKeywordNotifications
